import React, { useState, useRef } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Bug, Paperclip, X } from "@phosphor-icons/react";

/**
 * Iter208.5 — Feedback Beta button.
 * Bottone floating (bottom-right) visibile solo agli utenti autenticati.
 * Al click apre un modal con:
 *  - textarea messaggio (obbligatoria, max 500 char)
 *  - upload screenshot opzionale (immagine, max 5MB, preview)
 *  - cattura automatica di window.location.href e document.title
 * Submit → POST multipart/form-data su /api/feedback.
 */
const MAX_CHARS = 500;
const MAX_BYTES = 5 * 1024 * 1024;

export default function FeedbackButton() {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const reset = () => {
    setMessage("");
    setFile(null);
    setPreview("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const onSelectFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Il file deve essere un'immagine");
      e.target.value = "";
      return;
    }
    if (f.size > MAX_BYTES) {
      toast.error("Immagine troppo grande (max 5MB)");
      e.target.value = "";
      return;
    }
    setFile(f);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const removeFile = () => {
    setFile(null);
    setPreview("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    const msg = (message || "").trim();
    if (!msg) {
      toast.error("Scrivi un messaggio prima di inviare");
      return;
    }
    if (msg.length > MAX_CHARS) {
      toast.error(`Il messaggio non può superare ${MAX_CHARS} caratteri`);
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("message", msg);
      fd.append("page_url", window.location.href || "");
      fd.append("page_title", document.title || "");
      if (file) fd.append("screenshot", file, file.name);
      await api.post(`/feedback`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Grazie! Feedback inviato");
      reset();
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nell'invio");
    } finally {
      setSubmitting(false);
    }
  };

  const remaining = Math.max(0, MAX_CHARS - message.length);

  return (
    <>
      {/* Floating button */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-[80] inline-flex items-center gap-2 rounded-full bg-purple-600 hover:bg-purple-700 text-white shadow-lg hover:shadow-xl transition-all px-4 py-3 sm:px-5 sm:py-3 font-semibold text-sm group"
        data-testid="feedback-beta-btn"
        aria-label="Invia feedback beta"
        title="Invia feedback beta"
      >
        <Bug size={18} weight="bold" />
        <span className="hidden sm:inline">Feedback</span>
        <span className="hidden md:inline text-[10px] font-bold uppercase tracking-wide bg-white/20 rounded px-1.5 py-0.5">
          Beta
        </span>
      </button>

      <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); setOpen(false); } else setOpen(true); }}>
        <DialogContent className="max-w-md" data-testid="feedback-beta-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bug size={18} weight="bold" className="text-purple-600" />
              Feedback Beta
            </DialogTitle>
            <p className="text-xs text-slate-500 mt-1">
              Segnala bug o suggerimenti. Cattureremo automaticamente la pagina corrente.
            </p>
          </DialogHeader>

          <form onSubmit={submit} className="space-y-3">
            <div>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value.slice(0, MAX_CHARS + 50))}
                rows={5}
                placeholder="Descrivi il problema o il suggerimento…"
                data-testid="feedback-beta-message"
                autoFocus
                className={message.length > MAX_CHARS ? "border-red-400 focus-visible:ring-red-400" : ""}
              />
              <div className={"text-[11px] mt-1 text-right " + (message.length > MAX_CHARS ? "text-red-600 font-semibold" : "text-slate-500")}>
                {remaining} caratteri rimanenti
              </div>
            </div>

            {/* Upload screenshot */}
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">Screenshot (opzionale, max 5MB)</label>
              {preview ? (
                <div className="relative border rounded-lg overflow-hidden bg-slate-50">
                  <img src={preview} alt="Screenshot preview" className="w-full max-h-48 object-contain" data-testid="feedback-beta-preview" />
                  <button
                    type="button"
                    onClick={removeFile}
                    className="absolute top-1.5 right-1.5 bg-white/90 hover:bg-white text-slate-700 hover:text-red-600 rounded-full p-1 shadow"
                    aria-label="Rimuovi screenshot"
                    data-testid="feedback-beta-remove-file"
                  >
                    <X size={14} weight="bold" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="w-full inline-flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 hover:border-purple-400 hover:bg-purple-50/40 text-slate-600 rounded-lg py-3 text-sm font-medium transition-colors"
                  data-testid="feedback-beta-upload-btn"
                >
                  <Paperclip size={16} weight="bold" />
                  Allega uno screenshot
                </button>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onSelectFile}
                data-testid="feedback-beta-file-input"
              />
            </div>

            {/* Info riga contesto */}
            <div className="text-[11px] text-slate-500 bg-slate-50 rounded p-2 border">
              <div><span className="font-semibold">Pagina:</span> {document.title || "-"}</div>
              <div className="truncate"><span className="font-semibold">URL:</span> {window.location.href}</div>
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <Button
                type="button"
                variant="outline"
                onClick={() => { reset(); setOpen(false); }}
                data-testid="feedback-beta-cancel"
              >
                Annulla
              </Button>
              <Button
                type="submit"
                disabled={submitting || !message.trim() || message.length > MAX_CHARS}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="feedback-beta-submit"
              >
                {submitting ? "Invio…" : "Invia feedback"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
